// A change-driven list of the player's standing cities with no buildings. City clicks use the same build menu as
// globe clicks; the menu, not this list, owns option eligibility and sends builds through the HUD.

import type { CityIndex } from '@taxwar/net'
import type { MatchView } from '../match/contracts'
import { button, el } from './dom'
import type { WarMapIndex } from './warMap'

const BUTTON_LABEL = 'Empty cities'

export class EmptyCities {
  readonly element: HTMLElement
  readonly toggleButton: HTMLButtonElement
  private readonly list: HTMLUListElement
  private readonly rows = new Map<CityIndex, HTMLLIElement>()
  private ordersOpen = true

  constructor(
    private readonly map: WarMapIndex,
    private readonly match: MatchView,
    private readonly onSelect: (index: CityIndex, x: number, y: number) => void,
    private readonly onOpen: () => void,
    private readonly signal: AbortSignal,
  ) {
    this.element = el('section', 'tw-empty-cities tw-panel')
    this.element.id = 'tw-empty-cities'
    this.element.setAttribute('aria-label', 'Cities without buildings')
    this.element.hidden = true
    this.toggleButton = button('tw-button', BUTTON_LABEL)
    this.toggleButton.setAttribute('aria-controls', this.element.id)
    this.toggleButton.setAttribute('aria-expanded', 'false')
    this.toggleButton.addEventListener('click', () => this.setOpen(!this.open), { signal })
    this.list = el('ul', 'tw-empty-cities__list')
    this.element.append(el('h2', 'tw-heading', 'Cities without buildings'), this.list)
    for (const city of map.cities) if (this.eligible(city.index)) this.addCity(city.index)
    this.renderCount()
  }

  get open(): boolean {
    return !this.element.hidden
  }

  setOpen(open: boolean): void {
    if (open) this.onOpen()
    this.element.hidden = !open
    this.toggleButton.setAttribute('aria-expanded', open ? 'true' : 'false')
  }

  setOrdersOpen(open: boolean): void {
    this.ordersOpen = open
    if (!open) this.setOpen(false)
    this.renderCount()
  }

  cityChanged(index: CityIndex): void {
    const eligible = this.eligible(index)
    const existing = this.rows.get(index)
    if (eligible === (existing !== undefined)) return
    if (existing !== undefined) {
      existing.remove()
      this.rows.delete(index)
    } else {
      this.addCity(index)
    }
    this.renderCount()
  }

  private addCity(index: CityIndex): void {
    const row = el('li', 'tw-empty-cities__row')
    const option = button('tw-empty-cities__option', this.map.city(index).name)
    option.addEventListener('click', () => {
      const rect = option.getBoundingClientRect()
      this.setOpen(false)
      this.onSelect(index, rect.left, rect.top)
    }, { signal: this.signal })
    row.append(option)
    this.list.append(row)
    this.rows.set(index, row)
  }

  private eligible(index: CityIndex): boolean {
    const state = this.match.cityState(index)
    return state.owner === this.map.you && !state.fallen && state.buildings.length === 0
  }

  private renderCount(): void {
    const count = this.rows.size
    this.toggleButton.textContent = `${BUTTON_LABEL} (${count})`
    this.toggleButton.disabled = !this.ordersOpen || count === 0
    if (count === 0) this.setOpen(false)
  }
}
